<script setup lang="ts">
/**
 * Lightweight sequential tooltip tour. Positions each tooltip relative to
 * a DOM element matched by `step.selector`. Non-blocking: the pointer-events
 * layer is only on the tooltip card itself, so all underlying UI stays usable.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

interface TourStep {
  selector: string
  title: string
  body: string
}

const props = defineProps<{
  steps: TourStep[]
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  complete: []
}>()

const currentIndex = ref(0)
const anchorRect = ref<{
  top: number
  bottom: number
  left: number
  right: number
  width: number
  height: number
} | null>(null)

const currentStep = computed(() => props.steps[currentIndex.value])
const isLastStep = computed(() => currentIndex.value === props.steps.length - 1)

function refreshRect() {
  const el = document.querySelector(currentStep.value.selector)
  anchorRect.value = el ? el.getBoundingClientRect() : null
}

// Scroll/resize can fire many times per frame; coalesce into a single
// rAF-aligned recompute so we re-read layout at most once per paint (#104).
let rafId: number | null = null
function scheduleRefresh() {
  if (rafId !== null) return
  rafId = requestAnimationFrame(() => {
    rafId = null
    refreshRect()
  })
}

function next() {
  if (!isLastStep.value) {
    currentIndex.value++
    requestAnimationFrame(refreshRect)
  } else {
    finish()
  }
}

function finish() {
  // Emit only 'complete' — parent saves the tourSeen flag and then sets v-model to false.
  // Hiding before saving would race with page reloads and re-show the tour.
  emit('complete')
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      currentIndex.value = 0
      requestAnimationFrame(refreshRect)
    }
  }
)

onMounted(() => {
  if (props.modelValue) requestAnimationFrame(refreshRect)
  window.addEventListener('resize', scheduleRefresh, { passive: true })
  // Capture phase so we also catch scrolling inside nested scroll containers,
  // not just the window — anchorRect is viewport-relative, so any scroll that
  // moves the target must re-anchor the ring/tooltip. Covers touch scroll too.
  window.addEventListener('scroll', scheduleRefresh, { passive: true, capture: true })
})

onUnmounted(() => {
  window.removeEventListener('resize', scheduleRefresh)
  window.removeEventListener('scroll', scheduleRefresh, { capture: true })
  if (rafId !== null) cancelAnimationFrame(rafId)
})

// Tooltip position: prefer below the anchor, fall back to center if anchor not found.
const tooltipStyle = computed(() => {
  const r = anchorRect.value
  if (!r) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }

  const TOOLTIP_W = 280
  const TOOLTIP_H = 130 // approximate
  const GAP = 12

  const vw = window.innerWidth
  const vh = window.innerHeight

  let top = r.bottom + GAP
  let left = r.left

  // Flip above if not enough room below
  if (top + TOOLTIP_H > vh - 16) {
    top = r.top - TOOLTIP_H - GAP
  }
  if (top < 8) top = 8

  // Clamp horizontal
  if (left + TOOLTIP_W > vw - 8) left = vw - TOOLTIP_W - 8
  if (left < 8) left = 8

  return { top: `${top}px`, left: `${left}px`, width: `${TOOLTIP_W}px` }
})

// Spotlight ring around anchor
const ringStyle = computed(() => {
  const r = anchorRect.value
  if (!r) return { display: 'none' }
  const pad = 6
  return {
    top: `${r.top - pad}px`,
    left: `${r.left - pad}px`,
    width: `${r.width + pad * 2}px`,
    height: `${r.height + pad * 2}px`,
  }
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="fixed inset-0 z-50"
      style="pointer-events: none"
      data-testid="tour-overlay"
    >
      <!-- Spotlight ring on anchor -->
      <div
        class="absolute rounded-lg ring-2 ring-primary-400 ring-offset-2 animate-pulse"
        :style="ringStyle"
        aria-hidden="true"
      />

      <!-- Tooltip card -->
      <div
        class="absolute bg-card rounded-xl shadow-xl border border-border p-4"
        :style="tooltipStyle"
        style="pointer-events: auto"
        :data-testid="`tour-step-${currentIndex}`"
        role="dialog"
        aria-modal="false"
        :aria-label="currentStep.title"
      >
        <div class="flex items-start justify-between gap-2 mb-1">
          <p class="text-sm font-semibold text-foreground">{{ currentStep.title }}</p>
          <span class="text-xs text-muted-foreground shrink-0">
            {{ currentIndex + 1 }} / {{ steps.length }}
          </span>
        </div>
        <p class="text-xs text-muted-foreground mb-3">{{ currentStep.body }}</p>
        <div class="flex items-center justify-between gap-2">
          <button
            class="text-xs text-muted-foreground hover:text-foreground"
            data-testid="tour-skip"
            @click="finish"
          >
            Skip tour
          </button>
          <button
            class="px-3 py-1 bg-primary text-primary-foreground text-xs rounded-lg hover:bg-primary/90 transition-colors"
            :data-testid="isLastStep ? 'tour-done' : 'tour-next'"
            @click="next"
          >
            {{ isLastStep ? 'Done' : 'Next →' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
